import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Download, ArrowDownCircle, ArrowUpCircle, RefreshCw, Gift, AlertTriangle, CheckCircle2, Bell, TrendingUp, TrendingDown, Coins, Wallet, Users, Package, CircleDollarSign, Calendar, Lock, ShoppingBag, Filter, Phone, Mail, ChevronRight, Loader2, Share2, Copy, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { useGoldRate } from '../context/GoldRateContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import ReferralShareDialog from '../components/home-new/ReferralShareDialog';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const TransactionHistoryPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { buyRate, sellRate, rate22kt, loading: rateLoading } = useGoldRate();
  
  const [activeFilter, setActiveFilter] = useState('all');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [chartPeriod, setChartPeriod] = useState('1W');
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [showBookRate, setShowBookRate] = useState(true);
  const [showRefundRate, setShowRefundRate] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [passbookSummary, setPassbookSummary] = useState(null);
  const [downloadingStatement, setDownloadingStatement] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [vouchersLoading, setVouchersLoading] = useState(false);
  const [downloadingVoucher, setDownloadingVoucher] = useState(null);
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  
  // Filter panel state
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [dateRangePreset, setDateRangePreset] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  
  // Pagination state
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Redirect unauthenticated users to home-new silently
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/home-new', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch chart data from API
  useEffect(() => {
    const fetchChartData = async () => {
      setChartLoading(true);
      try {
        let days = 7;
        if (chartPeriod === '1D') days = 1;
        else if (chartPeriod === '1W') days = 7;
        else if (chartPeriod === '1M') days = 30;

        const response = await api.get(`/gold-rate/history?days=${days}&interval=daily`);
        const data = response.data;
        
        if (data.history && data.history.length > 0) {
          const chartData = data.history.map((item) => {
            const date = new Date(item.timestamp);
            return {
              date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
              bookRate: parseFloat((item.buy_rate || item.rate_22k / 1000).toFixed(2)),
              refundRate: parseFloat((item.sell_rate || (item.rate_22k / 1000) * 0.98).toFixed(2)),
            };
          });
          setChartData(chartData);
        }
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setChartLoading(false);
      }
    };
    
    fetchChartData();
  }, [chartPeriod]);

  // Fetch transactions from API (initial load)
  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const response = await api.get('/transactions?limit=20');
        setTransactions(response.data.transactions || []);
        setNextCursor(response.data.next_cursor || null);
        setHasMore(response.data.has_more || false);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        toast.error('Failed to load transactions');
        setTransactions([]);
        setNextCursor(null);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchTransactions();
    }
  }, [isAuthenticated]);

  // Load more transactions
  const loadMoreTransactions = async () => {
    if (!nextCursor || loadingMore) return;
    
    setLoadingMore(true);
    try {
      const response = await api.get(`/transactions?limit=20&cursor=${encodeURIComponent(nextCursor)}`);
      const newTransactions = response.data.transactions || [];
      
      setTransactions(prev => [...prev, ...newTransactions]);
      setNextCursor(response.data.next_cursor || null);
      setHasMore(response.data.has_more || false);
    } catch (error) {
      console.error('Error loading more transactions:', error);
      toast.error('Failed to load more transactions');
    } finally {
      setLoadingMore(false);
    }
  };

  // Fetch user journey stats
  useEffect(() => {
    const fetchUserStats = async () => {
      setStatsLoading(true);
      try {
        const response = await api.get('/user/journey-stats');
        console.log('Journey stats response:', response.data);
        setUserStats(response.data);
      } catch (error) {
        console.error('Error fetching user stats:', error);
        // Set empty stats object so UI shows 0s instead of breaking
        setUserStats({
          total_millis_bought: 0,
          total_millis_refunded: 0,
          total_millis_redeemed: 0,
          total_millis_gifted: 0,
          total_millis_received: 0,
          referral_earned: 0,
          current_balance: 0,
          total_invested: 0,
          current_value: 0
        });
      } finally {
        setStatsLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchUserStats();
    }
  }, [isAuthenticated]);

  // Fetch passbook summary for profit/loss calculation
  useEffect(() => {
    const fetchPassbookSummary = async () => {
      try {
        const response = await api.get('/passbook?limit=1');
        if (response.data?.summary) {
          setPassbookSummary(response.data.summary);
        }
      } catch (error) {
        console.error('Error fetching passbook summary:', error);
      }
    };

    if (isAuthenticated) {
      fetchPassbookSummary();
    }
  }, [isAuthenticated]);

  // Fetch vouchers when vouchers tab is selected
  useEffect(() => {
    const fetchVouchers = async () => {
      if (activeFilter !== 'vouchers') return;
      
      setVouchersLoading(true);
      try {
        const response = await api.get('/vouchers');
        setVouchers(response.data.vouchers || []);
      } catch (error) {
        console.error('Error fetching vouchers:', error);
        toast.error('Failed to load vouchers');
        setVouchers([]);
      } finally {
        setVouchersLoading(false);
      }
    };

    if (isAuthenticated && activeFilter === 'vouchers') {
      fetchVouchers();
    }
  }, [isAuthenticated, activeFilter]);

  // Show nothing while checking auth (prevents flash of content)
  if (authLoading || !isAuthenticated) {
    return null;
  }

  const currentRate = buyRate || 0;
  const currentRefundRate = sellRate || 0;
  const userMilliBalance = user?.milli_balance || 0;

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'buy', label: 'Book' },
    { id: 'redeem', label: 'Redeem' },
    { id: 'sell', label: 'Refund' },
    { id: 'referral', label: 'Referrals' },
    { id: 'vouchers', label: 'Vouchers' },
  ];

  const chartPeriods = ['1D', '1W', '1M'];

  // Calculate average rates from real data
  const avgBookRate = chartData.length > 0 
    ? (chartData.reduce((sum, d) => sum + d.bookRate, 0) / chartData.length).toFixed(2)
    : '0.00';
  const avgRefundRate = chartData.length > 0 
    ? (chartData.reduce((sum, d) => sum + d.refundRate, 0) / chartData.length).toFixed(2)
    : '0.00';

  // Helper function to get date range based on preset
  const getDateRange = (preset) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (preset) {
      case 'today':
        return { from: today, to: now };
      case '7days':
        return { from: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), to: now };
      case '30days':
        return { from: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), to: now };
      case '3months':
        return { from: new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000), to: now };
      case 'year':
        return { from: new Date(now.getFullYear(), 0, 1), to: now };
      case 'custom':
        return { 
          from: customDateFrom ? new Date(customDateFrom) : null, 
          to: customDateTo ? new Date(customDateTo + 'T23:59:59') : null 
        };
      default:
        return { from: null, to: null };
    }
  };

  // Count active filters
  const activeFilterCount = [
    dateRangePreset !== 'all',
    statusFilters.length > 0,
    amountMin !== '' || amountMax !== '',
    sortBy !== 'newest'
  ].filter(Boolean).length;

  // Clear all filters
  const clearAllFilters = () => {
    setDateRangePreset('all');
    setCustomDateFrom('');
    setCustomDateTo('');
    setStatusFilters([]);
    setAmountMin('');
    setAmountMax('');
    setSortBy('newest');
  };

  // Toggle status filter
  const toggleStatusFilter = (status) => {
    setStatusFilters(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  // Filter and sort transactions
  const filteredTransactions = (() => {
    // First filter by type (tabs)
    let filtered = activeFilter === 'all' 
      ? transactions 
      : activeFilter === 'vouchers'
        ? [] // Vouchers tab shows voucher cards, not transactions
        : activeFilter === 'referral'
          ? transactions.filter(t => ['referral', 'referral_reward', 'referral_bonus'].includes(t.type))
          : transactions.filter(t => t.type === activeFilter);

    // Apply date range filter
    const dateRange = getDateRange(dateRangePreset);
    if (dateRange.from) {
      filtered = filtered.filter(t => new Date(t.created_at) >= dateRange.from);
    }
    if (dateRange.to) {
      filtered = filtered.filter(t => new Date(t.created_at) <= dateRange.to);
    }

    // Apply status filter
    if (statusFilters.length > 0) {
      filtered = filtered.filter(t => statusFilters.includes(t.status));
    }

    // Apply amount range filter
    if (amountMin !== '') {
      filtered = filtered.filter(t => (t.amount_inr || 0) >= parseFloat(amountMin));
    }
    if (amountMax !== '') {
      filtered = filtered.filter(t => (t.amount_inr || 0) <= parseFloat(amountMax));
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'amount_high':
          return (b.amount_inr || 0) - (a.amount_inr || 0);
        case 'amount_low':
          return (a.amount_inr || 0) - (b.amount_inr || 0);
        default:
          return 0;
      }
    });

    return filtered;
  })();

  const getTypeIcon = (type, status) => {
    // If failed, show alert icon in red
    if (status === 'failed') {
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    }
    // If pending, show refresh icon in amber
    if (status === 'pending' || status === 'processing') {
      return <RefreshCw className="w-4 h-4 text-amber-500" />;
    }
    
    switch (type) {
      case 'book':
      case 'buy':
        return <ArrowDownCircle className="w-4 h-4 text-green-500" />;
      case 'sell':
      case 'refund':
        return <ArrowUpCircle className="w-4 h-4 text-[#B8860B]" />;
      case 'redeem':
        return <Package className="w-4 h-4 text-purple-600" />;
      case 'gift':
      case 'gift_sent':
        return <Gift className="w-4 h-4 text-pink-500" />;
      case 'gift_received':
        return <Gift className="w-4 h-4 text-green-500" />;
      case 'referral':
      case 'referral_reward':
      case 'referral_bonus':
        return <Users className="w-4 h-4 text-green-500" />;
      default:
        return <ArrowDownCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTypeIconBg = (type, status) => {
    // If failed, red background
    if (status === 'failed') {
      return 'bg-red-100';
    }
    // If pending, amber background
    if (status === 'pending' || status === 'processing') {
      return 'bg-amber-100';
    }
    
    switch (type) {
      case 'book':
      case 'buy':
        return 'bg-green-100';
      case 'sell':
      case 'refund':
        return 'bg-[#B8860B]/10';
      case 'redeem':
        return 'bg-purple-100';
      case 'gift':
      case 'gift_sent':
        return 'bg-pink-100';
      case 'gift_received':
        return 'bg-green-100';
      case 'referral':
      case 'referral_reward':
      case 'referral_bonus':
        return 'bg-green-100';
      default:
        return 'bg-gray-100';
    }
  };

  const getTypeLabel = (type, status) => {
    // If failed, show "Failed" label
    if (status === 'failed') return 'Failed';
    // If pending, show "Pending" label
    if (status === 'pending' || status === 'processing') return 'Pending';
    
    if (type === 'buy') return 'Booked';
    if (type === 'sell') return 'Refunded';
    if (type === 'referral_reward' || type === 'referral_bonus') return 'Referral';
    if (type === 'gift_sent') return 'Gift Sent';
    if (type === 'gift_received') return 'Gift Received';
    if (type === 'redeem') return 'Redeemed';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit',
      minute: '2-digit',
      hour12: true 
    });
  };

  const getTransactionDetails = (transaction) => {
    switch (transaction.type) {
      case 'book':
      case 'buy':
        return 'Gold booking via UPI';
      case 'sell':
      case 'refund':
        return 'Refund to bank account';
      case 'redeem':
        return 'Physical gold redemption';
      case 'gift':
      case 'gift_sent':
      case 'gift_received':
        return (transaction.milli_amount || 0) >= 0 ? 'Gift received' : 'Gift sent';
      case 'referral':
      case 'referral_reward':
      case 'referral_bonus':
        return 'Referral bonus earned';
      default:
        return 'Transaction';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-700 bg-green-100';
      case 'failed':
        return 'text-red-700 bg-red-100';
      case 'pending':
        return 'text-amber-700 bg-amber-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const todaysHigh = chartData.length > 0 ? Math.max(...chartData.map(d => d.bookRate), currentRate) : currentRate;
  const todaysLow = chartData.length > 0 ? Math.min(...chartData.map(d => d.bookRate), currentRate) : currentRate;

  // Build query params for filtered download
  const buildDownloadParams = () => {
    const params = new URLSearchParams();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    params.append('tz', timezone);

    // Tab type filter (activeFilter)
    if (activeFilter && activeFilter !== 'all' && activeFilter !== 'vouchers') {
      // Map frontend filter names to backend expected values
      const typeMap = {
        'buy': 'buy',
        'sell': 'sell', 
        'redeem': 'redeem',
        'referral': 'referral'
      };
      if (typeMap[activeFilter]) {
        params.append('transaction_type', typeMap[activeFilter]);
      }
    }

    // Date range filter
    const dateRange = getDateRange(dateRangePreset);
    if (dateRange.from && dateRangePreset !== 'all') {
      params.append('start_date', dateRange.from.toISOString().split('T')[0]);
    }
    if (dateRange.to && dateRangePreset !== 'all') {
      params.append('end_date', dateRange.to.toISOString().split('T')[0]);
    }

    // Status filter
    if (statusFilters.length > 0) {
      params.append('status', statusFilters.join(','));
    }

    // Amount range filter
    if (amountMin !== '') {
      params.append('amount_min', amountMin);
    }
    if (amountMax !== '') {
      params.append('amount_max', amountMax);
    }

    // Sort order
    if (sortBy && sortBy !== 'newest') {
      params.append('sort', sortBy);
    }

    return params.toString();
  };

  // Check if any filters are active (for button text)
  const hasActiveFilters = activeFilterCount > 0 || (activeFilter !== 'all' && activeFilter !== 'vouchers');

  // Download statement (passbook) as PDF with filters
  const handleDownloadStatement = async () => {
    setDownloadingStatement(true);
    try {
      const queryParams = buildDownloadParams();
      const response = await api.get(`/passbook/download?${queryParams}`);
      
      if (response.data.pdf_base64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || 'MintaMilli_Statement.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        const filterNote = response.data.filters_applied ? ' (filtered)' : '';
        toast.success(`Statement downloaded${filterNote}! ${response.data.total_records} transactions.`);
      }
    } catch (error) {
      console.error('Error downloading statement:', error);
      toast.error('Failed to download statement. Please try again.');
    } finally {
      setDownloadingStatement(false);
    }
  };

  // Download receipt for a specific transaction
  const handleDownloadReceipt = async (transaction) => {
    if (!transaction?.transaction_id) {
      toast.error('Transaction ID not found');
      return;
    }
    
    setDownloadingReceipt(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const txnType = transaction.type;
      
      // Determine the correct endpoint based on transaction type
      let endpoint;
      if (txnType === 'buy') {
        endpoint = `/voucher/receipt/${transaction.transaction_id}`;
      } else if (txnType === 'sell') {
        endpoint = `/voucher/refund/${transaction.transaction_id}`;
      } else {
        // For other types (redeem, gift, referral), use the generic receipt endpoint
        endpoint = `/voucher/receipt/${transaction.transaction_id}`;
      }
      
      const response = await api.get(`${endpoint}?tz=${encodeURIComponent(timezone)}`);
      
      if (response.data.pdf_base64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || `Receipt_${transaction.transaction_id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Receipt downloaded successfully!');
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
      if (error.response?.status === 404) {
        toast.error('Receipt not available for this transaction type');
      } else {
        toast.error('Failed to download receipt. Please try again.');
      }
    } finally {
      setDownloadingReceipt(false);
    }
  };

  // Download gold voucher PDF
  const handleDownloadVoucher = async (voucher) => {
    if (!voucher?.voucher_id) {
      toast.error('Voucher ID not found');
      return;
    }
    
    setDownloadingVoucher(voucher.voucher_id);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await api.get(`/voucher/gold/${voucher.voucher_id}?tz=${encodeURIComponent(timezone)}`);
      
      if (response.data.pdf_base64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || `GoldVoucher_${voucher.voucher_id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Voucher downloaded successfully!');
      }
    } catch (error) {
      console.error('Error downloading voucher:', error);
      toast.error('Failed to download voucher. Please try again.');
    } finally {
      setDownloadingVoucher(null);
    }
  };

  // Get voucher status
  const getVoucherStatus = (voucher) => {
    if (voucher.status === 'redeemed' || voucher.redeemed) {
      return { label: 'Redeemed', color: 'bg-purple-100 text-purple-700', icon: 'check' };
    }
    
    const expiryDate = new Date(voucher.expires_at || voucher.expiry_date);
    const now = new Date();
    
    if (expiryDate < now) {
      return { label: 'Expired', color: 'bg-red-100 text-red-700', icon: 'x' };
    }
    
    // Calculate days remaining
    const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining <= 7) {
      return { label: `${daysRemaining}d left`, color: 'bg-amber-100 text-amber-700', icon: 'clock' };
    }
    
    return { label: 'Active', color: 'bg-green-100 text-green-700', icon: 'check' };
  };

  // Format expiry date
  const formatExpiryDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  return (
    <div className="px-3 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
      {/* Content wrapper with same alignment as home page hero */}
      <div className="ml-0 lg:ml-[5%] xl:ml-[80px]">
        {/* Page Title */}
        <div className="mb-6">
          <h1 
            className="text-5xl sm:text-6xl font-normal tracking-wide mb-2 text-[#B8860B]" 
            style={{ fontFamily: 'Georgia, serif', textShadow: '2px 2px 4px rgba(0,0,0,0.1)' }}
          >
            Transaction History
          </h1>
          <p className="text-gray-600 text-base sm:text-lg">Track your Mint a Milli journey.</p>
        </div>

        {/* Gold Rate Chart Section */}
        <div className="bg-white/90 rounded-2xl p-4 shadow-sm border border-amber-100 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 min-h-[420px]">
            {/* Left - Price Info + Chart + Banner (2/3 Width) */}
            <div className="lg:w-2/3 flex flex-col">
              {/* Price + Chart Row */}
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                {/* Price Info Box (Left ~25%) */}
                <div className="sm:w-1/4 flex flex-col justify-center">
                  {/* Gold Icon */}
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-xl flex items-center justify-center mb-3 shadow-md">
                    <Coins className="w-6 h-6 text-white" />
                  </div>
                  
                  <p className="text-sm text-gray-500 font-medium mb-1">Live Gold Price</p>
                  
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                    ₹{currentRate.toFixed(2)} <span className="text-base font-normal text-gray-500">/Milli</span>
                  </p>
                  
                  {/* Percentage Change Badge */}
                  <div className="flex items-center gap-1 mt-1">
                    <span className="flex items-center gap-1 text-sm font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      <TrendingUp className="w-3.5 h-3.5" />
                      +2.58% (24K)
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-2">22KT: ₹{rate22kt.toLocaleString('en-IN')} / g</p>

                  {/* Legend Toggles */}
                  <div className="flex flex-col gap-2 mt-4">
                    <button
                      onClick={() => setShowBookRate(!showBookRate)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                        showBookRate
                          ? 'bg-amber-50 border-amber-400 text-amber-700'
                          : 'bg-gray-50 border-gray-200 text-gray-400'
                      }`}
                    >
                      <span className={`w-3 h-3 rounded-full ${showBookRate ? 'bg-amber-500' : 'bg-gray-300'}`}></span>
                      Avg Book Rate
                    </button>
                    <button
                      onClick={() => setShowRefundRate(!showRefundRate)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                        showRefundRate
                          ? 'bg-green-50 border-green-400 text-green-700'
                          : 'bg-gray-50 border-gray-200 text-gray-400'
                      }`}
                    >
                      <span className={`w-3 h-3 rounded-full ${showRefundRate ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                      Avg Refund Rate
                    </button>
                  </div>
                </div>

                {/* Chart Area (Right ~75%) */}
                <div className="sm:w-3/4 flex flex-col">
                  {/* Period Selector */}
                  <div className="flex gap-1.5 mb-3">
                    {chartPeriods.map((period) => (
                      <button
                        key={period}
                        onClick={() => setChartPeriod(period)}
                        className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
                          chartPeriod === period
                            ? 'bg-[#004D40] text-white shadow-md'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {period}
                      </button>
                    ))}
                  </div>

                  {/* Chart */}
                  <div className="flex-1 min-h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11, fill: '#666' }}
                          axisLine={{ stroke: '#e0e0e0' }}
                          padding={{ left: 10, right: 10 }}
                          interval="preserveStartEnd"
                        />
                        <YAxis 
                          domain={['dataMin - 0.5', 'dataMax + 0.5']}
                          tick={{ fontSize: 13, fill: '#555', fontWeight: 500 }}
                          axisLine={{ stroke: '#e0e0e0' }}
                          tickFormatter={(value) => `₹${value.toFixed(1)}`}
                          width={50}
                          padding={{ top: 20, bottom: 20 }}
                        />
                        <Tooltip 
                          formatter={(value, name) => [`₹${value}`, name === 'bookRate' ? 'Avg Book Rate' : 'Avg Refund Rate']}
                          labelStyle={{ color: '#333', fontSize: '13px' }}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '13px' }}
                        />
                        {showBookRate && (
                          <Line 
                            type="monotone" 
                            dataKey="bookRate" 
                            stroke="#f59e0b" 
                            strokeWidth={2}
                            dot={{ fill: '#f59e0b', strokeWidth: 1, r: 3 }}
                            activeDot={{ r: 5, fill: '#f59e0b' }}
                          />
                        )}
                        {showRefundRate && (
                          <Line 
                            type="monotone" 
                            dataKey="refundRate" 
                            stroke="#22c55e" 
                            strokeWidth={2}
                            dot={{ fill: '#22c55e', strokeWidth: 1, r: 3 }}
                            activeDot={{ r: 5, fill: '#22c55e' }}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Average Rates Stats */}
                  <div className="flex gap-6 mt-2 pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                      <span className="text-sm text-gray-600">Avg Book Rate:</span>
                      <span className="text-sm font-bold text-amber-600">₹{avgBookRate} / Milli</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                      <span className="text-sm text-gray-600">Avg Refund Rate:</span>
                      <span className="text-sm font-bold text-green-600">₹{avgRefundRate} / Milli</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature Banner */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="grid grid-cols-5 gap-3">
                  {/* Auto Buy - Coming Soon */}
                  <div className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl border border-gray-200 relative opacity-70">
                    <div className="absolute -top-2 -right-2 bg-[#B8860B] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      SOON
                    </div>
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                      <Calendar className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-sm font-semibold text-gray-400">Auto Buy</p>
                    <p className="text-xs text-gray-400 mt-1">Schedule automatic gold purchases</p>
                  </div>

                  {/* Book Gold */}
                  <div className="flex flex-col items-center text-center p-3 bg-[#004D40]/10 rounded-xl border-2 border-[#004D40] hover:bg-[#004D40]/20 transition-colors cursor-pointer">
                    <div className="w-10 h-10 bg-[#004D40] rounded-full flex items-center justify-center mb-2">
                      <Lock className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-sm font-semibold text-[#004D40]">Book Gold</p>
                    <p className="text-xs text-gray-500 mt-1">Securely store your digital gold</p>
                  </div>

                  {/* Price Alerts */}
                  <div className="flex flex-col items-center text-center p-3 bg-[#004D40]/10 rounded-xl border-2 border-[#004D40] hover:bg-[#004D40]/20 transition-colors cursor-pointer">
                    <div className="w-10 h-10 bg-[#004D40] rounded-full flex items-center justify-center mb-2">
                      <Bell className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-sm font-semibold text-[#004D40]">Price Alerts</p>
                    <p className="text-xs text-gray-500 mt-1">Get notified on price movements</p>
                  </div>

                  {/* Gift Gold - Coming Soon */}
                  <div className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl border border-gray-200 relative opacity-70">
                    <div className="absolute -top-2 -right-2 bg-[#B8860B] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      SOON
                    </div>
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                      <Gift className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-sm font-semibold text-gray-500">Gift Gold</p>
                    <p className="text-xs text-gray-400 mt-1">Send gold to loved ones</p>
                  </div>

                  {/* Redeem Gold */}
                  <div className="flex flex-col items-center text-center p-3 bg-[#004D40]/10 rounded-xl border-2 border-[#004D40] hover:bg-[#004D40]/20 transition-colors cursor-pointer">
                    <div className="w-10 h-10 bg-[#004D40] rounded-full flex items-center justify-center mb-2">
                      <ShoppingBag className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-sm font-semibold text-[#004D40]">Redeem Gold</p>
                    <p className="text-xs text-gray-500 mt-1">Convert to physical gold</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Summary Box (1/3 Width) */}
            <div className="lg:w-1/3">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 h-full">
                {/* Title with Icon */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Wallet className="w-4 h-4 text-amber-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">Account Summary</h3>
                </div>
                
                {statsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Stats Grid with Icons */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {/* Total Booked */}
                      <div className="bg-[#F5E6C8]/50 rounded-lg p-3 border border-[#B8860B]/20">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-[#B8860B]/20 rounded-md flex items-center justify-center">
                            <ArrowDownCircle className="w-3.5 h-3.5 text-[#B8860B]" />
                          </div>
                          <p className="text-xs text-gray-500">Total Booked</p>
                        </div>
                        <p className="text-base font-bold text-gray-900 text-center">{userStats?.total_millis_bought?.toFixed(2) || '0.00'} <span className="text-xs font-normal text-gray-500">Milli</span></p>
                      </div>
                      
                      {/* Total Refunded */}
                      <div className="bg-[#F5E6C8]/50 rounded-lg p-3 border border-[#B8860B]/20">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-[#B8860B]/20 rounded-md flex items-center justify-center">
                            <ArrowUpCircle className="w-3.5 h-3.5 text-[#B8860B]" />
                          </div>
                          <p className="text-xs text-gray-500">Total Refunded</p>
                        </div>
                        <p className="text-base font-bold text-gray-900 text-center">{userStats?.total_millis_refunded?.toFixed(2) || '0.00'} <span className="text-xs font-normal text-gray-500">Milli</span></p>
                      </div>
                      
                      {/* Total Redeemed */}
                      <div className="bg-[#F5E6C8]/50 rounded-lg p-3 border border-[#B8860B]/20">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-[#B8860B]/20 rounded-md flex items-center justify-center">
                            <Package className="w-3.5 h-3.5 text-[#B8860B]" />
                          </div>
                          <p className="text-xs text-gray-500">Total Redeemed</p>
                        </div>
                        <p className="text-base font-bold text-gray-900 text-center">{userStats?.total_millis_redeemed?.toFixed(2) || '0.00'} <span className="text-xs font-normal text-gray-500">Milli</span></p>
                      </div>
                      
                      {/* Total Gifted */}
                      <div className="bg-[#F5E6C8]/50 rounded-lg p-3 border border-[#B8860B]/20">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-[#B8860B]/20 rounded-md flex items-center justify-center">
                            <Gift className="w-3.5 h-3.5 text-[#B8860B]" />
                          </div>
                          <p className="text-xs text-gray-500">Total Gifted</p>
                        </div>
                        <p className="text-base font-bold text-gray-900 text-center">{userStats?.total_millis_gifted?.toFixed(2) || '0.00'} <span className="text-xs font-normal text-gray-500">Milli</span></p>
                      </div>
                      
                      {/* Gifts Received */}
                      <div className="bg-[#F5E6C8]/50 rounded-lg p-3 border border-[#B8860B]/20">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-[#B8860B]/20 rounded-md flex items-center justify-center">
                            <Gift className="w-3.5 h-3.5 text-[#B8860B]" />
                          </div>
                          <p className="text-xs text-gray-500">Gifts Received</p>
                        </div>
                        <p className="text-base font-bold text-gray-900 text-center">{userStats?.total_millis_received?.toFixed(2) || '0.00'} <span className="text-xs font-normal text-gray-500">Milli</span></p>
                      </div>
                      
                      {/* Referral Earned */}
                      <div className="bg-[#F5E6C8]/50 rounded-lg p-3 border border-[#B8860B]/20">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-[#B8860B]/20 rounded-md flex items-center justify-center">
                            <Users className="w-3.5 h-3.5 text-[#B8860B]" />
                          </div>
                          <p className="text-xs text-gray-500">Referral Earned</p>
                        </div>
                        <p className="text-base font-bold text-gray-900 text-center">{userStats?.referral_earned?.toFixed(2) || '0.00'} <span className="text-xs font-normal text-gray-500">Milli</span></p>
                      </div>
                    </div>
                    
                    {/* Current Balance */}
                    <div className="bg-gradient-to-r from-[#F5E6C8] to-[#FDEBCF] rounded-lg p-3 border border-[#B8860B]/30 mb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-[#B8860B]/20 rounded-lg flex items-center justify-center">
                            <Coins className="w-4 h-4 text-[#B8860B]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Current Balance</p>
                            <p className="text-xl font-bold text-gray-900">{userStats?.current_balance?.toFixed(2) || userMilliBalance.toFixed(2)} <span className="text-sm font-normal text-gray-500">Milli&apos;s</span></p>
                            {/* Current value in INR */}
                            {(passbookSummary?.current_refund_rate || sellRate) && (
                              <p className="text-sm text-gray-600">
                                ≈ ₹{((userStats?.current_balance || userMilliBalance) * (passbookSummary?.current_refund_rate || sellRate)).toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Coins className="w-5 h-5 text-[#B8860B]" />
                          <Coins className="w-5 h-5 text-[#DAA520]" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Profit/Loss - Using passbook data with correct formula */}
                    {(() => {
                      // Use passbook's profit_loss_percent which is calculated as:
                      // ((current_refund_rate - avg_book_rate) / avg_book_rate) * 100
                      const profitLossPercent = passbookSummary?.profit_loss_percent || 0;
                      const avgBookRate = passbookSummary?.avg_book_rate || 0;
                      const currentRefundRate = passbookSummary?.current_refund_rate || sellRate || 0;
                      const currentBalance = userStats?.current_balance || userMilliBalance;
                      
                      // Calculate INR profit/loss based on rate difference
                      const currentValue = currentBalance * currentRefundRate;
                      const investedValue = currentBalance * avgBookRate;
                      const profitLossInr = currentValue - investedValue;
                      const isProfit = profitLossPercent >= 0;
                      
                      // Only show if we have valid data
                      if (!avgBookRate || avgBookRate <= 0) {
                        return null;
                      }
                      
                      return (
                        <div className={`${isProfit ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} rounded-lg p-3 border flex items-center justify-between`}>
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 ${isProfit ? 'bg-green-100' : 'bg-red-100'} rounded-lg flex items-center justify-center`}>
                              {isProfit ? (
                                <TrendingUp className="w-4 h-4 text-green-600" />
                              ) : (
                                <TrendingDown className="w-4 h-4 text-red-600" />
                              )}
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Profit / Loss</p>
                              <p className={`text-base font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                                {isProfit ? '+' : ''}₹{Math.abs(profitLossInr).toFixed(2)}
                              </p>
                            </div>
                          </div>
                          <p className={`text-lg font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                            {isProfit ? '+' : ''}{profitLossPercent.toFixed(2)}%
                          </p>
                        </div>
                      );
                    })()}
                    
                    {/* Avg Book Rate Card */}
                    {passbookSummary?.avg_book_rate > 0 && (
                      <div className="bg-[#F5E6C8]/50 rounded-lg p-3 border border-[#B8860B]/20 mt-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-[#B8860B]/20 rounded-lg flex items-center justify-center">
                            <BarChart3 className="w-4 h-4 text-[#B8860B]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Avg Book Rate</p>
                            <p className="text-base font-bold text-gray-900">₹{passbookSummary.avg_book_rate.toFixed(2)}/Milli</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

      {/* Transaction History Section */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left - Transaction Table (75%) */}
          <div className="lg:w-[75%] bg-white/90 rounded-2xl p-6 shadow-sm border border-amber-100">
            {/* Header with Title and Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Transaction History</h2>
              <div className="flex gap-2">
                <button 
                  onClick={handleDownloadStatement}
                  disabled={downloadingStatement || filteredTransactions.length === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors border disabled:opacity-50 disabled:cursor-not-allowed ${
                    hasActiveFilters
                      ? 'bg-[#004D40] text-white border-[#004D40] hover:bg-[#00302B]'
                      : 'bg-[#004D40]/10 text-[#004D40] border-[#004D40]/30 hover:bg-[#004D40]/20'
                  }`}
                  title={hasActiveFilters ? 'Download will include only filtered transactions' : 'Download all transactions'}
                >
                  {downloadingStatement ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {downloadingStatement 
                    ? 'Downloading...' 
                    : hasActiveFilters 
                      ? `Download (${filteredTransactions.length})` 
                      : 'Download Statement'
                  }
                </button>
                <div className="relative">
                  <button 
                    onClick={() => setShowFilterPanel(!showFilterPanel)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                      showFilterPanel || activeFilterCount > 0
                        ? 'bg-[#004D40] text-white border-[#004D40]'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <Filter className="w-4 h-4" />
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-[#B8860B] text-white text-xs rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>

                  {/* Filter Panel Dropdown */}
                  {showFilterPanel && (
                    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                      {/* Panel Header */}
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-900">Filters</h3>
                        <button 
                          onClick={() => setShowFilterPanel(false)}
                          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>

                      <div className="p-4 max-h-[400px] overflow-y-auto">
                        {/* Date Range Section */}
                        <div className="mb-4">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Date Range
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { id: 'all', label: 'All Time' },
                              { id: 'today', label: 'Today' },
                              { id: '7days', label: 'Last 7 Days' },
                              { id: '30days', label: 'Last 30 Days' },
                              { id: '3months', label: 'Last 3 Months' },
                              { id: 'year', label: 'This Year' },
                            ].map(preset => (
                              <button
                                key={preset.id}
                                onClick={() => {
                                  setDateRangePreset(preset.id);
                                  if (preset.id !== 'custom') {
                                    setCustomDateFrom('');
                                    setCustomDateTo('');
                                  }
                                }}
                                className={`px-3 py-2 text-xs font-medium rounded-lg transition-all ${
                                  dateRangePreset === preset.id
                                    ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                                }`}
                              >
                                {preset.label}
                              </button>
                            ))}
                          </div>
                          {/* Custom Date Range */}
                          <div className="mt-2">
                            <button
                              onClick={() => setDateRangePreset('custom')}
                              className={`w-full px-3 py-2 text-xs font-medium rounded-lg transition-all ${
                                dateRangePreset === 'custom'
                                  ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                              }`}
                            >
                              Custom Range
                            </button>
                            {dateRangePreset === 'custom' && (
                              <div className="flex gap-2 mt-2">
                                <input
                                  type="date"
                                  value={customDateFrom}
                                  onChange={(e) => setCustomDateFrom(e.target.value)}
                                  className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300"
                                  placeholder="From"
                                />
                                <input
                                  type="date"
                                  value={customDateTo}
                                  onChange={(e) => setCustomDateTo(e.target.value)}
                                  className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300"
                                  placeholder="To"
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Status Section */}
                        <div className="mb-4">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Status
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { id: 'completed', label: 'Completed', color: 'green' },
                              { id: 'pending', label: 'Pending', color: 'yellow' },
                              { id: 'processing', label: 'Processing', color: 'blue' },
                              { id: 'failed', label: 'Failed', color: 'red' },
                            ].map(status => (
                              <button
                                key={status.id}
                                onClick={() => toggleStatusFilter(status.id)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                                  statusFilters.includes(status.id)
                                    ? status.color === 'green' ? 'bg-green-100 text-green-800 border border-green-300'
                                    : status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                                    : status.color === 'blue' ? 'bg-blue-100 text-blue-800 border border-blue-300'
                                    : 'bg-red-100 text-red-800 border border-red-300'
                                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                                }`}
                              >
                                {status.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Amount Range Section */}
                        <div className="mb-4">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                            <Coins className="w-3.5 h-3.5" />
                            Amount Range (₹)
                          </p>
                          <div className="flex gap-2 items-center">
                            <input
                              type="number"
                              value={amountMin}
                              onChange={(e) => setAmountMin(e.target.value)}
                              placeholder="Min"
                              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300"
                            />
                            <span className="text-gray-400">—</span>
                            <input
                              type="number"
                              value={amountMax}
                              onChange={(e) => setAmountMax(e.target.value)}
                              placeholder="Max"
                              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300"
                            />
                          </div>
                        </div>

                        {/* Sort By Section */}
                        <div className="mb-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                            <TrendingUp className="w-3.5 h-3.5" />
                            Sort By
                          </p>
                          <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                          >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                            <option value="amount_high">Amount (High → Low)</option>
                            <option value="amount_low">Amount (Low → High)</option>
                          </select>
                        </div>
                      </div>

                      {/* Panel Footer */}
                      <div className="flex gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
                        <button
                          onClick={clearAllFilters}
                          className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Clear All
                        </button>
                        <button
                          onClick={() => setShowFilterPanel(false)}
                          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#004D40] rounded-lg hover:bg-[#00302B] transition-colors"
                        >
                          Apply Filters
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeFilter === filter.id
                      ? 'bg-[#004D40] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {filter.label}
                  {filter.id === 'vouchers' && vouchers.length > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-white/30 rounded-full text-xs">
                      {vouchers.filter(v => {
                        const status = getVoucherStatus(v);
                        return status.label === 'Active' || status.label.includes('left');
                      }).length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Vouchers Grid (shown when vouchers tab is active) */}
            {activeFilter === 'vouchers' ? (
              <div>
                {vouchersLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                  </div>
                ) : vouchers.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Package className="w-8 h-8 text-amber-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Vouchers Yet</h3>
                    <p className="text-gray-500 mb-4">Redeem your Milli&apos;s to get gold vouchers</p>
                    <button 
                      onClick={() => navigate('/redeem-gold')}
                      className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-white rounded-lg font-medium hover:from-yellow-600 hover:to-amber-600 transition-all"
                    >
                      Redeem Now
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {vouchers.map((voucher) => {
                      const status = getVoucherStatus(voucher);
                      const isActive = status.label === 'Active' || status.label.includes('left');
                      
                      return (
                        <div 
                          key={voucher.voucher_id}
                          className={`bg-white rounded-xl border-2 ${isActive ? 'border-amber-200' : 'border-gray-200'} p-4 relative overflow-hidden`}
                        >
                          {/* Gold pattern overlay for active vouchers */}
                          {isActive && (
                            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-amber-100/50 to-transparent rounded-bl-full" />
                          )}
                          
                          <div className="relative">
                            {/* Header with Status Badge */}
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActive ? 'bg-amber-100' : 'bg-gray-100'}`}>
                                  <Package className={`w-5 h-5 ${isActive ? 'text-amber-600' : 'text-gray-400'}`} />
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Voucher ID</p>
                                  <p className={`text-sm font-semibold ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                                    {voucher.voucher_id}
                                  </p>
                                </div>
                              </div>
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                                {status.label}
                              </span>
                            </div>

                            {/* Gold Amount */}
                            <div className={`rounded-lg p-3 mb-3 ${isActive ? 'bg-gradient-to-r from-amber-50 to-yellow-50' : 'bg-gray-50'}`}>
                              <p className="text-xs text-gray-500 mb-1">Gold Amount</p>
                              <div className="flex items-baseline gap-2">
                                <span className={`text-2xl font-bold ${isActive ? 'text-amber-700' : 'text-gray-500'}`}>
                                  {(voucher.gold_grams || voucher.milli_amount / 1000 || 0).toFixed(3)}
                                </span>
                                <span className={`text-sm ${isActive ? 'text-amber-600' : 'text-gray-400'}`}>grams</span>
                                <span className="text-xs text-gray-400 ml-auto">
                                  ({(voucher.milli_amount || voucher.gold_grams * 1000 || 0).toFixed(0)} Milli)
                                </span>
                              </div>
                            </div>

                            {/* Details Row */}
                            <div className="flex items-center justify-between text-sm mb-4">
                              <div>
                                <p className="text-xs text-gray-500">Created</p>
                                <p className={`font-medium ${isActive ? 'text-gray-700' : 'text-gray-400'}`}>
                                  {formatExpiryDate(voucher.created_at)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">Expires</p>
                                <p className={`font-medium ${isActive ? 'text-gray-700' : 'text-gray-400'}`}>
                                  {formatExpiryDate(voucher.expires_at || voucher.expiry_date)}
                                </p>
                              </div>
                            </div>

                            {/* QR Code Placeholder & Download Button */}
                            <div className="flex items-center gap-3">
                              <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${isActive ? 'bg-gray-100' : 'bg-gray-50'}`}>
                                {/* QR Code placeholder - would need actual QR generation */}
                                <div className="grid grid-cols-4 gap-0.5">
                                  {[...Array(16)].map((_, i) => (
                                    <div 
                                      key={i} 
                                      className={`w-2.5 h-2.5 ${Math.random() > 0.5 ? (isActive ? 'bg-gray-800' : 'bg-gray-400') : 'bg-transparent'}`} 
                                    />
                                  ))}
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="text-xs text-gray-500 mb-2">
                                  Present this voucher at any Mothers Gold showroom
                                </p>
                                <button
                                  onClick={() => handleDownloadVoucher(voucher)}
                                  disabled={downloadingVoucher === voucher.voucher_id || !isActive}
                                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                    isActive 
                                      ? 'bg-[#004D40]/10 text-[#004D40] hover:bg-[#004D40]/20 border border-[#004D40]/30'
                                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  }`}
                                >
                                  {downloadingVoucher === voucher.voucher_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                  {downloadingVoucher === voucher.voucher_id ? 'Downloading...' : 'Download PDF'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Transaction Table */}
                <div className="overflow-hidden rounded-xl border border-gray-200">
                  <div className="max-h-[380px] overflow-y-auto">
                    <table className="w-full table-fixed">
                      <thead className="sticky top-0 bg-gray-50 z-10">
                        <tr>
                          <th className="w-[14%] text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="w-[14%] text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date & Time</th>
                          <th className="w-[18%] text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</th>
                          <th className="w-[12%] text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="w-[12%] text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Rate</th>
                          <th className="w-[12%] text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Value</th>
                          <th className="w-[18%] text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={7} className="text-center py-8 text-gray-500">Loading...</td>
                          </tr>
                        ) : filteredTransactions.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-8 text-gray-500">No transactions found</td>
                          </tr>
                        ) : (
                          filteredTransactions.map((transaction, index) => (
                            <tr 
                              key={transaction._id || transaction.transaction_id || index} 
                              className={`hover:bg-amber-50/50 cursor-pointer transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                              onClick={() => setSelectedTransaction(transaction)}
                            >
                              <td className="py-4 px-4 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getTypeIconBg(transaction.type, transaction.status)}`}>
                                    {getTypeIcon(transaction.type, transaction.status)}
                                  </div>
                                  <span className="text-sm font-medium text-gray-900">{getTypeLabel(transaction.type || '', transaction.status)}</span>
                                </div>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <p className="text-sm font-medium text-gray-900">{formatDateShort(transaction.created_at)}</p>
                                <p className="text-xs text-gray-500">{formatTime(transaction.created_at)}</p>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <p className="text-sm text-gray-600">{getTransactionDetails(transaction)}</p>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <span className={`text-sm font-semibold ${
                                  transaction.status === 'failed' ? 'text-red-500' :
                                  transaction.status === 'pending' || transaction.status === 'processing' ? 'text-amber-500' :
                                  (transaction.milli_amount || 0) >= 0 ? 'text-green-600' : 'text-[#B8860B]'
                                }`}>
                                  {transaction.status === 'failed' ? '' : (transaction.milli_amount || 0) >= 0 ? '+' : ''}{(transaction.milli_amount || 0).toFixed(2)} M
                                </span>
                              </td>
                              <td className="py-4 px-4 text-center text-sm text-gray-600">
                                ₹{(transaction.rate_at_time || transaction.rate || 0).toFixed(2)}/M
                              </td>
                              <td className="py-4 px-4 text-center text-sm font-medium text-gray-900">
                                ₹{(transaction.amount_inr || 0).toFixed(2)}
                              </td>
                              <td className="py-4 px-4 text-center">
                                <span className={`inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                                  {transaction.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                                  {transaction.status}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination Info & Load More */}
                {filteredTransactions.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                      <span>Showing {filteredTransactions.length} of {transactions.length} transactions</span>
                      {hasMore && (
                        <span className="text-amber-600">{hasMore ? 'More available' : ''}</span>
                      )}
                    </div>
                    
                    {/* Load More Button */}
                    {hasMore && activeFilter === 'all' && (
                      <button 
                        onClick={loadMoreTransactions}
                        disabled={loadingMore}
                        className="w-full py-3 bg-[#004D40]/10 text-[#004D40] font-medium rounded-xl flex items-center justify-center gap-2 hover:bg-[#004D40]/20 transition-colors border border-[#004D40]/30 disabled:opacity-50"
                      >
                        {loadingMore ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            Load More Transactions
                            <ChevronRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    )}
                    
                    {/* Note when filters are applied */}
                    {activeFilter !== 'all' && hasMore && (
                      <p className="text-xs text-gray-400 text-center mt-2">
                        Clear filters to load more transactions
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right - Sidebar (25%) */}
          <div className="lg:w-[25%] flex flex-col gap-4">
            {/* Referral Card */}
            <div className="bg-gradient-to-br from-[#F5E6C8] to-[#FDEBCF] rounded-2xl p-6 border-2 border-[#B8860B]/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#B8860B]/20 rounded-xl flex items-center justify-center border border-[#B8860B]/30">
                  <Gift className="w-6 h-6 text-[#B8860B]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#8B6914]">Grow More, Earn More!</h3>
                  <p className="text-sm text-[#A67C00]">Refer your friends & earn gold rewards</p>
                </div>
              </div>
              <p className="text-sm text-[#8B6914]/80 mb-4">
                Share your unique referral code and earn Milli&apos;s when your friends make their first booking.
              </p>
              <button 
                onClick={() => setShowReferralDialog(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white rounded-xl font-semibold hover:from-[#A67C00] hover:to-[#C4972A] transition-all shadow-md"
              >
                <Share2 className="w-4 h-4" />
                Share & Earn
              </button>
            </div>

            {/* Contact Card */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-sm text-gray-500 mb-4">Our support team is here to help you with any questions.</p>
              
              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-4 h-4 text-gray-500" />
                  </div>
                  <span>support@mothersgold.in</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-4 h-4 text-gray-500" />
                  </div>
                  <span>+91 8137830916</span>
                </div>
              </div>
              
              <button className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">Transaction Details</h2>
              <button 
                onClick={() => setSelectedTransaction(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Transaction Type Header - Full Width */}
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getTypeIconBg(selectedTransaction.type, selectedTransaction.status)}`}>
                  {getTypeIcon(selectedTransaction.type, selectedTransaction.status)}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-lg">{getTypeLabel(selectedTransaction.type || '', selectedTransaction.status)} Transaction</p>
                  <p className={`text-base font-medium ${
                    selectedTransaction.status === 'failed' ? 'text-red-500' :
                    selectedTransaction.status === 'pending' || selectedTransaction.status === 'processing' ? 'text-amber-500' :
                    (selectedTransaction.milli_amount || 0) >= 0 ? 'text-green-600' : 'text-[#B8860B]'
                  }`}>
                    {selectedTransaction.status === 'failed' ? '' : (selectedTransaction.milli_amount || 0) >= 0 ? '+' : ''}{(selectedTransaction.milli_amount || 0).toFixed(2)} Milli&apos;s
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">₹{(selectedTransaction.amount_inr || 0).toFixed(2)}</p>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedTransaction.status)}`}>
                    {selectedTransaction.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                    {selectedTransaction.status === 'failed' && <AlertTriangle className="w-3 h-3" />}
                    {selectedTransaction.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Left - Transaction Info (3/5) */}
                <div className="lg:col-span-3">
                  {/* Details Grid */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-500 text-sm">Date & Time</span>
                      <span className="font-medium text-gray-900">{formatDate(selectedTransaction.created_at)}</span>
                    </div>
                    
                    {/* Transaction ID with Copy */}
                    <div className="py-2 border-b border-gray-100">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-gray-500 text-sm">Transaction ID</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(selectedTransaction.transaction_id);
                            toast.success('Transaction ID copied!');
                          }}
                          className="text-amber-600 hover:text-amber-700 text-xs flex items-center gap-1"
                        >
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      </div>
                      <p className="font-mono text-sm text-gray-900 break-all bg-gray-50 px-2 py-1 rounded">
                        {selectedTransaction.transaction_id}
                      </p>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-500 text-sm">Payment Method</span>
                      <span className="font-medium text-gray-900 capitalize">{(selectedTransaction.payment_method || 'N/A').replace('_', ' ')}</span>
                    </div>
                    
                    {(selectedTransaction.rate_at_time || selectedTransaction.rate) && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-500 text-sm">Gold Rate</span>
                        <span className="font-medium text-gray-900">₹{(selectedTransaction.rate_at_time || selectedTransaction.rate)?.toFixed(2)}/Milli</span>
                      </div>
                    )}
                    
                    {selectedTransaction.note && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-500 text-sm">Note</span>
                        <span className="font-medium text-gray-900 text-right max-w-[60%]">{selectedTransaction.note}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right - Transaction Journey (2/5) */}
                <div className="lg:col-span-2 lg:border-l lg:border-gray-100 lg:pl-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Transaction Journey</h3>
                  <div className="space-y-3">
                    {(() => {
                      // Define journey steps based on transaction type
                      const getJourneySteps = (type, status) => {
                        const baseTime = selectedTransaction.created_at;
                        
                        switch (type) {
                          case 'buy':
                            return [
                              { label: 'Payment Initiated', time: baseTime },
                              { label: 'Payment Successful', time: baseTime },
                              { label: 'Milli Booked', time: baseTime },
                              { label: 'Completed', time: baseTime },
                            ];
                          case 'sell':
                            return [
                              { label: 'Refund Requested', time: baseTime },
                              { label: 'Milli Debited', time: baseTime },
                              { label: 'Bank Transfer Initiated', time: baseTime },
                              { label: 'Completed', time: baseTime },
                            ];
                          case 'redeem':
                            return [
                              { label: 'Redemption Requested', time: baseTime },
                              { label: 'Milli Debited', time: baseTime },
                              { label: 'Voucher Generated', time: baseTime },
                              { label: 'Completed', time: baseTime },
                            ];
                          case 'referral':
                          case 'referral_reward':
                          case 'referral_bonus':
                            return [
                              { label: 'Referral Verified', time: baseTime },
                              { label: 'Reward Approved', time: baseTime },
                              { label: 'Milli Credited', time: baseTime },
                              { label: 'Completed', time: baseTime },
                            ];
                          case 'gift_sent':
                            return [
                              { label: 'Gift Initiated', time: baseTime },
                              { label: 'Milli Debited', time: baseTime },
                              { label: 'Gift Sent', time: baseTime },
                              { label: 'Completed', time: baseTime },
                            ];
                          case 'gift_received':
                            return [
                              { label: 'Gift Received', time: baseTime },
                              { label: 'Verified', time: baseTime },
                              { label: 'Milli Credited', time: baseTime },
                              { label: 'Completed', time: baseTime },
                            ];
                          default:
                            return [
                              { label: 'Initiated', time: baseTime },
                              { label: 'Processing', time: baseTime },
                              { label: 'Verified', time: baseTime },
                              { label: 'Completed', time: baseTime },
                            ];
                        }
                      };

                      // Determine how many steps are completed based on status
                      const getCompletedSteps = (status) => {
                        switch (status) {
                          case 'completed':
                            return 4;
                          case 'processing':
                            return 2;
                          case 'pending':
                            return 1;
                          case 'failed':
                            return 1;
                          default:
                            return 0;
                        }
                      };

                      const steps = getJourneySteps(selectedTransaction.type, selectedTransaction.status);
                      const completedSteps = getCompletedSteps(selectedTransaction.status);
                      const isFailed = selectedTransaction.status === 'failed';

                      return steps.map((step, index) => {
                        const isCompleted = index < completedSteps;
                        const isActive = index === completedSteps - 1;
                        const showFailedState = isFailed && index === 0;
                        
                        return (
                          <div key={index} className="flex items-start gap-2">
                            <div className="flex flex-col items-center">
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                showFailedState ? 'bg-red-500' :
                                isCompleted ? 'bg-green-500' : 'bg-gray-300'
                              }`}>
                                <CheckCircle2 className="w-3 h-3 text-white" />
                              </div>
                              {index < steps.length - 1 && (
                                <div className={`w-0.5 h-6 ${
                                  showFailedState ? 'bg-red-300' :
                                  isCompleted && index < completedSteps - 1 ? 'bg-green-500' : 'bg-gray-300'
                                }`} />
                              )}
                            </div>
                            <div className="pb-2">
                              <p className={`text-sm font-medium ${
                                showFailedState ? 'text-red-600' :
                                isCompleted ? 'text-gray-900' : 'text-gray-400'
                              }`}>
                                {showFailedState ? 'Failed' : step.label}
                              </p>
                              {isCompleted && (
                                <p className="text-xs text-gray-500">{formatDate(step.time)}</p>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>

              {/* Download Receipt Button - Full Width at Bottom */}
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button 
                  onClick={() => handleDownloadReceipt(selectedTransaction)}
                  disabled={downloadingReceipt || !['buy', 'sell'].includes(selectedTransaction.type)}
                  className="w-full py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 hover:from-amber-600 hover:to-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-300 disabled:to-gray-400"
                >
                  {downloadingReceipt ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                  {downloadingReceipt ? 'Downloading...' : 
                    !['buy', 'sell'].includes(selectedTransaction.type) ? 'Receipt Not Available' : 'Download Receipt'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Referral Share Dialog */}
      <ReferralShareDialog
        isOpen={showReferralDialog}
        onClose={() => setShowReferralDialog(false)}
        referralCode={userStats?.referral_code || user?.referral_code || 'MILLI0000'}
        shareMessage={userStats?.share_message}
        shareUrl={userStats?.share_url}
      />
    </div>
  );
};

export default TransactionHistoryPage;
